// The English text of each niche's page (/comecar/:nicho?lng=en): the site's English niche pages (keepo.bio/en/for/…)
// send there. Same keys and shape as nicheCopy.ts; prices stay in reais, as on the rest of the page.
import { AdNicheCopy } from "./nicheCopy";

const item = (name: string, detail: string) => ({ name, detail });

export const AD_NICHE_COPY_EN: Record<string, AdNicheCopy> = {
  psicologos: {
    title: "Your psychology site in 1 minute",
    subtitle:
      "AI reads your Instagram and builds your site: your approach, how sessions work and contact in one link, without writing from scratch.",
    example: {
      name: "Ana Lima",
      bio: "Psychologist · online and in person",
      buttons: ["Book a session", "WhatsApp", "How therapy works"],
      aboutTitle: "About me",
      about: "I see teenagers and adults with careful listening, at your own pace.",
      headline: "A safe space to take care of yourself",
      section: "Sessions",
      items: [item("Individual therapy", "50 min"), item("Couples therapy", "80 min")],
      quote: "I felt welcome from the very first session.",
      quoteBy: "Patient",
    },
  },
  nutricionistas: {
    title: "Your nutrition site in 1 minute",
    subtitle: "AI reads your Instagram and builds your site: specialties, consultations and booking in one place.",
    example: {
      name: "Júlia Ramos",
      bio: "Sports nutritionist",
      buttons: ["Book a consultation", "WhatsApp", "Plans"],
      aboutTitle: "About me",
      about: "Meal plans made for your routine and your workouts.",
      headline: "Eating well without complicating your routine",
      section: "Consultations",
      items: [item("First consultation", "60 min"), item("Follow-up", "30 min")],
      quote: "I learned to eat better without suffering.",
      quoteBy: "Patient",
    },
  },
  dentistas: {
    title: "Your dental site in 1 minute",
    subtitle:
      "AI reads your Instagram and builds your site: treatments, insurance, address and booking on a page that loads fast on phones.",
    example: {
      name: "Clínica Sorriso Leve",
      bio: "Dentistry for the whole family",
      buttons: ["Book a check-up", "Treatments", "Directions"],
      aboutTitle: "The clinic",
      about: "From the first baby tooth to whitening, with unhurried care.",
      headline: "Smiles cared for with calm and kindness",
      section: "Treatments",
      items: [item("Cleaning", "from R$ 180"), item("Whitening", "from R$ 690")],
      quote: "My son left the appointment smiling.",
      quoteBy: "Pedro's mom",
    },
  },
  fisioterapeutas: {
    title: "Your physical therapy site in 1 minute",
    subtitle:
      "AI reads your Instagram and builds your site: services, studio and contact always at hand for whoever wants to start.",
    example: {
      name: "Rafael Souza",
      bio: "Physical therapy and pilates",
      buttons: ["Book an assessment", "WhatsApp", "Where I work"],
      aboutTitle: "About me",
      about: "Rehabilitation and injury prevention, with attention to every movement.",
      headline: "Move again without pain",
      section: "Services",
      items: [item("Physical therapy", "50 min"), item("Pilates", "55 min")],
      quote: "I went back to running after years.",
      quoteBy: "Patient",
    },
  },
  personalTrainers: {
    title: "Your personal trainer site in 1 minute",
    subtitle:
      "AI reads your Instagram and builds your site: plans, results and sign-up in one link, no more answering DM after DM.",
    example: {
      name: "Bruno Alves",
      bio: "Personal trainer",
      buttons: ["I want to train", "WhatsApp", "Plans"],
      aboutTitle: "About me",
      about: "Workouts built for your goal, in person or online.",
      headline: "Your training, your way",
      section: "Plans",
      items: [item("In person", "3x a week"), item("Online", "workouts + adjustments")],
      quote: "In three months my life changed.",
      quoteBy: "Student",
    },
  },
  esteticistas: {
    title: "Your beauty clinic site in 1 minute",
    subtitle:
      "AI reads your Instagram and builds your site: treatments, prices and booking on a page that looks like your brand.",
    example: {
      name: "Studio Pele Bela",
      bio: "Facial and body aesthetics",
      buttons: ["Book a time", "Treatments", "Prices"],
      aboutTitle: "The studio",
      about: "Treatments done with skill and care, in a space made to relax.",
      headline: "Your skin, truly cared for",
      section: "Treatments",
      items: [item("Facial cleansing", "R$ 150"), item("Lymphatic drainage", "R$ 120")],
      quote: "I left feeling renewed.",
      quoteBy: "Client",
    },
  },
  advogados: {
    title: "Your law firm site in 1 minute",
    subtitle:
      "AI reads your Instagram and builds your site: practice areas, background and contact on a sober, informative page.",
    example: {
      name: "Carvalho Advocacia",
      bio: "Family and labor law",
      buttons: ["Talk to a lawyer", "Practice areas", "The firm"],
      aboutTitle: "The firm",
      about: "Clear, close guidance, from the first contact to the solution.",
      headline: "Your rights explained clearly",
      section: "Areas",
      items: [item("Family", "divorce and custody"), item("Labor", "severance and overtime")],
      quote: "They explained everything without legalese.",
      quoteBy: "Client",
    },
  },
  medicos: {
    title: "Your medical site in 1 minute",
    subtitle:
      "AI reads your Instagram and builds your site: specialties, insurance and booking on a page that's clear for patients.",
    example: {
      name: "Paulo Mendes",
      bio: "General practitioner",
      buttons: ["Book an appointment", "Insurance", "The office"],
      aboutTitle: "About me",
      about: "Attentive care, with time to listen and explain.",
      headline: "Care with time to listen",
      section: "Appointments",
      items: [item("Appointment", "40 min"), item("Check-up", "tests and follow-up")],
      quote: "Finally a doctor who listens.",
      quoteBy: "Patient",
    },
  },
  terapeutas: {
    title: "Your therapy site in 1 minute",
    subtitle: "AI reads your Instagram and builds your site: your practices and sessions presented calmly and clearly.",
    example: {
      name: "Lua Terapias",
      bio: "Reiki and holistic therapies",
      buttons: ["Book a session", "WhatsApp", "Therapies"],
      aboutTitle: "About me",
      about: "Practices to balance body and mind, at your own pace.",
      headline: "Balance for body and mind",
      section: "Therapies",
      items: [item("Reiki", "60 min"), item("Aromatherapy", "45 min")],
      quote: "I leave every session feeling lighter.",
      quoteBy: "Client",
    },
  },
  profissionaisDeSaude: {
    title: "Your health site in 1 minute",
    subtitle: "AI reads your Instagram and builds your site: services, locations and contact in one link.",
    example: {
      name: "Espaço Bem Viver",
      bio: "Integrated health",
      buttons: ["Book an appointment", "Specialties", "Directions"],
      aboutTitle: "The space",
      about: "Professionals from many fields taking care of you in one place.",
      headline: "Your health in good hands",
      section: "Specialties",
      items: [item("Nutrition", "60 min consultation"), item("Psychology", "50 min session")],
      quote: "Caring service from start to finish.",
      quoteBy: "Patient",
    },
  },
  coachesEMentores: {
    title: "Your mentoring site in 1 minute",
    subtitle:
      "AI reads your Instagram and builds your site: method, programs and results, with the way to a conversation always in sight.",
    example: {
      name: "Carla Nunes",
      bio: "Career mentor",
      buttons: ["Book a call", "Programs", "Testimonials"],
      aboutTitle: "About me",
      about: "I help professionals take their next step with method and clarity.",
      headline: "Your next step, clearly",
      section: "Programs",
      items: [item("1:1 mentoring", "8 sessions"), item("Group", "6 weeks")],
      quote: "I left with a real plan.",
      quoteBy: "Mentee",
    },
  },
  corretoresDeImoveis: {
    title: "Your real estate site in 1 minute",
    subtitle:
      "AI reads your Instagram and builds your site: your listings up front and contact for whoever wants a visit.",
    example: {
      name: "Lucas Torres",
      bio: "Buying, selling and renting",
      buttons: ["See listings", "WhatsApp", "Value my property"],
      aboutTitle: "About me",
      about: "I help you find the right place, no rush and no surprises.",
      headline: "The right home for you",
      section: "Featured",
      items: [item("2-bedroom apartment", "Downtown"), item("House with a yard", "Jardins")],
      quote: "I found my apartment in two weeks.",
      quoteBy: "Client",
    },
  },
  arquitetos: {
    title: "Your architecture site in 1 minute",
    subtitle: "AI reads your Instagram and builds your site: projects, process and quote requests in an elegant link.",
    example: {
      name: "Atelier Linha",
      bio: "Architecture and interiors",
      buttons: ["Request a quote", "Projects", "Process"],
      aboutTitle: "The studio",
      about: "Projects that bring beauty and function together, from concept to construction.",
      headline: "Spaces that feel like you",
      section: "Services",
      items: [item("Interiors", "per room"), item("Full renovation", "on request")],
      quote: "Our home turned out just as we dreamed.",
      quoteBy: "Client",
    },
  },
  fotografos: {
    title: "Your photographer site in 1 minute",
    subtitle:
      "AI reads your Instagram and builds your site: portfolio, packages and quote requests one tap from your bio.",
    example: {
      name: "Marina Luz",
      bio: "Family and event photography",
      buttons: ["Request a quote", "Portfolio", "Packages"],
      aboutTitle: "About me",
      about: "Natural photos that keep the moment just as it was.",
      headline: "Moments that last forever",
      section: "Packages",
      items: [item("Family session", "1h + 30 photos"), item("Event", "from 3h")],
      quote: "The photos turned out beautiful!",
      quoteBy: "Client",
    },
  },
  negociosDeAlimentacao: {
    title: "Your restaurant site in 1 minute",
    subtitle:
      "AI reads your Instagram and builds your site: a menu with photos and prices, and orders straight on WhatsApp.",
    example: {
      name: "Cantina da Vila",
      bio: "Home-style food and delivery",
      buttons: ["Order now", "Menu", "Opening hours"],
      aboutTitle: "The cantina",
      about: "Food made fresh, with a homemade touch.",
      headline: "Home cooking, done right",
      section: "Menu",
      items: [item("Dish of the day", "R$ 32"), item("Lasagna", "R$ 45")],
      quote: "Best lunch box in the neighborhood.",
      quoteBy: "Customer",
    },
  },
  lojas: {
    title: "Your store site in 1 minute",
    subtitle:
      "AI reads your Instagram and builds your site: new arrivals and sales channels together to lead customers to buy.",
    example: {
      name: "Ateliê Bia",
      bio: "Women's fashion",
      buttons: ["Buy on WhatsApp", "New arrivals", "Address"],
      aboutTitle: "The store",
      about: "Hand-picked pieces, with personal service.",
      headline: "New arrivals every week",
      section: "Featured",
      items: [item("Midi dress", "R$ 189"), item("Linen shirt", "R$ 149")],
      quote: "Impeccable service.",
      quoteBy: "Customer",
    },
  },
  contadores: {
    title: "Your accounting site in 1 minute",
    subtitle:
      "AI reads your Instagram and builds your site: your services in plain language and organized contact from new clients.",
    example: {
      name: "Prisma Contabilidade",
      bio: "Accounting for small businesses",
      buttons: ["Talk to an accountant", "Services", "Open a company"],
      aboutTitle: "The firm",
      about: "We take care of the numbers so you can take care of your business.",
      headline: "Your company up to date, no headaches",
      section: "Services",
      items: [item("Opening a company", "from R$ 490"), item("Monthly fee", "from R$ 290")],
      quote: "I finally understood my taxes.",
      quoteBy: "Client",
    },
  },
  tatuadores: {
    title: "Your tattoo studio site in 1 minute",
    subtitle: "AI reads your Instagram and builds your site: portfolio, quotes and schedule in one link.",
    example: {
      name: "Estúdio Traço Fino",
      bio: "Fineline and blackwork",
      buttons: ["Request a quote", "Portfolio", "This month's schedule"],
      aboutTitle: "The studio",
      about: "Every piece is drawn from scratch, from your idea.",
      headline: "Your idea, drawn just for you",
      section: "Styles",
      items: [item("Fineline", "from R$ 300"), item("Blackwork", "from R$ 450")],
      quote: "It turned out exactly as I imagined.",
      quoteBy: "Client",
    },
  },
  petShops: {
    title: "Your pet shop site in 1 minute",
    subtitle: "AI reads your Instagram and builds your site: services, prices by size and booking for pet parents.",
    example: {
      name: "Pet Amigo",
      bio: "Bath, grooming and vet",
      buttons: ["Book a bath", "Services", "Directions"],
      aboutTitle: "The pet shop",
      about: "Care and affection for your best friend.",
      headline: "Your pet well cared for and happy",
      section: "Services",
      items: [item("Bath", "from R$ 50"), item("Bath and grooming", "from R$ 80")],
      quote: "Thor always comes back smelling great.",
      quoteBy: "Thor's owner",
    },
  },
  igrejas: {
    title: "Your church site in 1 minute",
    subtitle:
      "AI reads your Instagram and builds your site: services, address and livestreams in a link that's easy to share.",
    example: {
      name: "Comunidade Esperança",
      bio: "Christian church",
      buttons: ["Service times", "Watch live", "Directions"],
      aboutTitle: "Who we are",
      about: "A community with open doors for you and your family.",
      headline: "Come as you are",
      section: "Services",
      items: [item("Sunday", "10am and 7pm"), item("Wednesday", "8pm")],
      quote: "I found a family here.",
      quoteBy: "Member",
    },
  },
  artistas: {
    title: "Your site in 1 minute",
    subtitle: "AI reads your Instagram and builds your site: releases, shows and booking contact in one link.",
    example: {
      name: "Dani Som",
      bio: "Singer and songwriter",
      buttons: ["Listen now", "Upcoming shows", "Book me"],
      aboutTitle: "About me",
      about: "Brazilian music with fresh arrangements, live and on streaming.",
      headline: "Music to listen and dance to",
      section: "Upcoming shows",
      items: [item("Friday", "Bar do Centro"), item("Saturday", "Festival da Vila")],
      quote: "Amazing show from start to finish.",
      quoteBy: "Fan",
    },
  },
  freelancers: {
    title: "Your freelancer site in 1 minute",
    subtitle:
      "AI reads your Instagram and builds your site: portfolio, services and contact in a digital business card.",
    example: {
      name: "Léo Design",
      bio: "Designer and illustrator",
      buttons: ["Request a quote", "Portfolio", "Services"],
      aboutTitle: "About me",
      about: "Visual identity and illustration for brands that want to stand out.",
      headline: "Design that tells your story",
      section: "Services",
      items: [item("Visual identity", "from R$ 1,200"), item("Illustration", "on request")],
      quote: "Understood my brand right away.",
      quoteBy: "Client",
    },
  },
};
